import React, { useState, useEffect } from 'react';
import { Select, Table } from 'antd';
const { Option } = Select;

const MyTable = ({
  dataSource = [],
  columns = [],
  handleTableChange,
  loading = false,
  onChange = null,
  pagination: controlledPagination = null,
  rowSelection = null,
  rowKey = 'id',
  scroll = { x: 1200 },
  size = 'middle',
  locale = undefined
}) => {
  const [localPagination, setLocalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const tableChangeHandler = handleTableChange || onChange;
  const effectivePagination = controlledPagination || localPagination;
  const totalRecords = controlledPagination?.total ?? dataSource.length;
  const pageSize = effectivePagination.pageSize || 10;
  const currentPage = effectivePagination.current || 1;
  const isServerPaginated = Boolean(controlledPagination) && totalRecords > dataSource.length;

  useEffect(() => {
    if (!controlledPagination) {
      setLocalPagination(prev => ({
        ...prev,
        total: dataSource.length,
        current: 1
      }));
    }
  }, [controlledPagination, dataSource.length]);

  const renderPagination = () => {
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const currentStart = totalRecords > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
    const currentEnd = Math.min(currentPage * pageSize, totalRecords);
    const availableOptions = [5, 10, 25, 50, 100].filter((value, index, arr) => value <= totalRecords || value === 10 || arr[index - 1] < totalRecords);

    const updatePagination = (nextPagination) => {
      if (!controlledPagination) {
        setLocalPagination(nextPagination);
      }

      if (tableChangeHandler) {
        tableChangeHandler(nextPagination, {}, {});
      }
    };
    
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 px-2">
        <div className="flex items-center gap-2 justify-center sm:justify-start">
          <span className="text-sm text-gray-600">
            {totalRecords > 0
              ? `${currentStart}-${currentEnd} of ${totalRecords} items`
              : '0 items'
            }
          </span>
        </div>
        
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-center gap-2">
            <Select
              value={pageSize >= totalRecords && totalRecords > 0 ? 'all' : pageSize}
              onChange={(value) => {
                const newPageSize = value === 'all' ? totalRecords : parseInt(value);
                updatePagination({
                  current: 1,
                  pageSize: newPageSize || 10,
                  total: totalRecords
                });
              }}
              style={{ width: 130 }}
              size="small"
            >
              {availableOptions.map(option => (
                <Option key={option} value={option}>{option} / page</Option>
              ))}
              {totalRecords > 0 && <Option value="all">All</Option>}
            </Select>
          </div>
          
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => {
                  updatePagination({
                    ...effectivePagination,
                    current: currentPage - 1,
                    pageSize,
                    total: totalRecords
                  });
                }}
                className="px-2 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              <span className="text-sm px-2">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                disabled={currentPage === totalPages}
                onClick={() => {
                  updatePagination({
                    ...effectivePagination,
                    current: currentPage + 1,
                    pageSize,
                    total: totalRecords
                  });
                }}
                className="px-2 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getPaginatedData = () => {
    if (isServerPaginated || pageSize >= dataSource.length) {
      return dataSource;
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return dataSource.slice(startIndex, endIndex);
  };

  const paginatedData = getPaginatedData();

  return (
    <div>
      <Table 
        columns={columns} 
        dataSource={paginatedData} 
        rowKey={rowKey}
        rowSelection={rowSelection || undefined}
        pagination={false}
        loading={loading}
        onChange={(paginationConfig, filters, sorter) => {
          if (tableChangeHandler) {
            tableChangeHandler(effectivePagination, filters, sorter);
          }
        }}
        scroll={scroll}
        size={size}
        locale={locale}
      />
      
      {totalRecords > 0 && renderPagination()}
    </div>
  );
};

export default MyTable;
